import React, { Suspense, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, useRoutes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { PublicClientApplication, EventType } from '@azure/msal-browser'
import { MsalProvider, useMsal } from '@azure/msal-react'

// Brand fonts (Montserrat + IBM Plex Mono), then CoreUI Pro compiled with the Scryer
// brand. coreui.scss REPLACES @coreui/coreui-pro/dist/css/coreui.min.css - importing
// both ships CoreUI twice and leaves the app half-branded.
import '@scryerlabs/branding/fonts.css'
import '@scryerlabs/ui/coreui.scss'

// Custom styles
import './styles/custom.scss'

// i18n
import './i18n/i18n'

// Auth
import { msalConfig } from './config/auth'
import RequireAuthentication from './components/auth/RequireAuthentication'
import { initializeTokenService } from './services/access-token-service'

// Context providers
import { ThemeProvider } from '@scryerlabs/ui'
import { ToastProvider, AuthorizationProvider } from './context'

// Routes
import routes from './routes/routes'

// Loading spinner for lazy-loaded components
import { AppErrorBoundary, LoadingSpinner } from './components/common'

// A deploy replaces the hashed route chunks, so a tab left open across one fails on its next lazy
// route. A reload fetches the current build. The time guard stops a chunk missing from that build
// too from reloading forever, and lets the error boundary show the failure instead.
const chunkReloadKey = 'chunk-reload-at'
window.addEventListener('vite:preloadError', () => {
  if (Date.now() - Number(sessionStorage.getItem(chunkReloadKey)) < 10_000) return
  sessionStorage.setItem(chunkReloadKey, String(Date.now()))
  window.location.reload()
})

const msalInstance = new PublicClientApplication(msalConfig)

msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const account = (event.payload as { account?: { homeAccountId: string } }).account
    if (account) {
      msalInstance.setActiveAccount(msalInstance.getAccountByHomeId(account.homeAccountId))
    }
  }
})

const accounts = msalInstance.getAllAccounts()
if (accounts.length > 0) {
  msalInstance.setActiveAccount(accounts[0])
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false, // Manual refresh only
    },
  },
})

const AppRoutes: React.FC = () => {
  const element = useRoutes(routes)
  return element
}

const AuthTokenInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance } = useMsal()

  useEffect(() => {
    initializeTokenService(instance)
  }, [instance])

  return <>{children}</>
}

const App: React.FC = () => {
  return (
    <React.StrictMode>
      <BrowserRouter>
        <MsalProvider instance={msalInstance}>
          <QueryClientProvider client={queryClient}>
            {/* Dark by default, preserved deliberately rather than inherited: this is a
                staff tool and the people in it expect dark. The front office uses
                "system" instead, which suits a customer-facing portal. */}
            <ThemeProvider defaultTheme="dark">
              <ToastProvider>
                <AppErrorBoundary>
                  <RequireAuthentication>
                    <AuthTokenInitializer>
                      <AuthorizationProvider>
                        <Suspense fallback={<LoadingSpinner fullPage />}>
                          <AppRoutes />
                        </Suspense>
                      </AuthorizationProvider>
                    </AuthTokenInitializer>
                  </RequireAuthentication>
                </AppErrorBoundary>
              </ToastProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </MsalProvider>
      </BrowserRouter>
    </React.StrictMode>
  )
}

createRoot(document.getElementById('root')!).render(<App />)
